// Canonical copy. `make sync-launch-context` copies this directory into both web test
// apps (`web-app/lib/api-traffic`, `ctv-web-app/src/api-traffic`) and
// `check-launch-context-sync.sh` fails the commit if a copy has drifted — the same
// mechanism the shared launch-context module uses. Edit this file, then run the sync.
//
// Keep it framework-free: web-app is Next.js (server-rendered, so every `window` access
// needs a guard) and ctv-web-app is a Vite SPA. The guards are inert in the SPA, which is
// what lets one file serve both byte for byte.
import {
  NamiAPITrafficRecord,
  registerAPITrafficObserver,
} from "@namiml/web-sdk";

/**
 * The nami command that turns capture on in the SDK.
 *
 * Push this into `namiCommands` at **every** `Nami.configure` call site in this app.
 * Registering the observer is not enough on its own: the SDK gates capture on this
 * command, and this app configures from several places (`index`, `config`, `staging`).
 * Android's port of this tab shipped with the flag set on one of its two configure paths
 * and the feature silently dead on the other (NAM-4447).
 */
export const API_TRAFFIC_COMMAND = "apiTrafficObserver";

/** How many records the tab keeps. Oldest are dropped first. */
export const RECORD_LIMIT = 200;

/** One row as the tab renders it: a traffic record plus the sequence number it was given. */
export type TrafficRow = NamiAPITrafficRecord & { seq: number };

/**
 * A record as it appears in the transcript the e2e harness reads.
 *
 * Byte-compatible with what the Apple and Android test apps write, so one assertion engine
 * (`scripts/e2e-checks/api-transcript-assert.mjs`) reads all three.
 *
 * Deliberately omits `requestBody` and `responseBody`. The suites assert the *sequence* of
 * calls, not their contents, and those bodies carry the host-supplied `external_id`
 * (`login`) and purchase identifiers (`transaction`) — writing them into an artifact the
 * harness archives is a far larger exposure than the assertions need.
 */
type TranscriptEntry = {
  seq: number;
  timestampMs: number;
  kind: string;
  method?: string;
  url?: string;
  path?: string;
  statusCode?: number;
  durationMs?: number;
  marker?: string;
  /** On a `configure-result` marker: the NamiConfigurationState the call returned. */
  state?: string;
  /** On a `paywall-fetch-telemetry` marker: the SDK's `Paywall fetch telemetry:` line. */
  message?: string;
};

/**
 * The line core logs once per paywall resolution (NAM-4357). The paywall-cache e2e suite grades
 * it; `paywall-cache-pull-telemetry.sh` reads it back out of the transcript.
 */
const PAYWALL_FETCH_TELEMETRY = "Paywall fetch telemetry:";

const TRANSCRIPT_KEY = "nami.apiTrafficTranscript";

/** Where the harness reads the transcript from. See NAM-4611 for the retrieval wiring. */
declare global {
  interface Window {
    __namiApiTranscript?: TranscriptEntry[];
  }
}

type Listener = (rows: TrafficRow[]) => void;

/**
 * Query parameter that points the store at an e2e transcript sink.
 *
 * The e2e harness cannot read `window.__namiApiTranscript`: a browser has no pullable app
 * container the way a simulator or an emulator does, and Maestro's `runScript` runs in Maestro's
 * own JS runtime rather than in the page. So when this parameter is present the store PUSHES the
 * transcript to the URL it names (`scripts/e2e-checks/transcript-sink.mjs`), and
 * `first-run-pull-transcript.sh` GETs it from there. See NAM-4611.
 *
 * Absent — which is every run that is not an e2e suite, including production — none of this code
 * does anything.
 */
const SINK_PARAM = "transcriptSink";

/**
 * Coalescing window for publishes. A cold start fans out one request per paywall body (~150 on
 * staging); without this the store would issue a POST per record.
 */
const PUBLISH_DEBOUNCE_MS = 150;

/**
 * Bodies at or over this many characters skip `keepalive`: Chrome refuses a keepalive (or beacon)
 * body past ~64 KiB outright. Kept under the quota with margin for multi-byte characters.
 */
const KEEPALIVE_BODY_LIMIT = 60_000;

/**
 * In-memory ring buffer of traffic records, plus the e2e transcript.
 *
 * A module singleton rather than React state: the SDK emits records from whatever code
 * path is running, including before the tab has ever been opened, and the records must
 * survive client-side navigation between pages.
 */
class ApiTrafficStore {
  private rows: TrafficRow[] = [];
  private transcript: TranscriptEntry[] = [];
  private listeners = new Set<Listener>();
  private seq = 0;
  /** Set once the buffer has dropped at least one record, so the UI can say so. */
  private dropped = false;
  private started = false;
  /** The e2e transcript sink, or null when not running under the harness. */
  private sink: string | null = null;
  private publishTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Monotonic publish counter. Publishes are fire-and-forget, so two can land out of order; the
   * sink drops a body whose `rev` is not greater than the one it holds. Without it the sink could
   * end up holding the older, SHORTER transcript with nothing left to correct it.
   *
   * Seeded from `Date.now()` rather than 0: the sink's lifetime is the FLOW (it is cleared once
   * per flow by the harness's pre-flow hook), while this counter's lifetime is a single PAGE
   * LOAD. `launchApp: clearState: true` and any later reload/navigation inside the same flow are
   * each a fresh page load, so a second load in the same flow must start ahead of whatever the
   * first load already published — starting at 0 again would have every one of its publishes
   * silently dropped as stale, leaving the sink stuck serving the first load's transcript.
   */
  private publishRev = Date.now();

  /**
   * Registers with the SDK and begins recording.
   *
   * Call once, from `_app.tsx`, so capture does not depend on which page happened to
   * configure the SDK — this app configures from `index`, `config` and `staging`.
   */
  start(): void {
    if (this.started || typeof window === "undefined") return;
    this.started = true;
    this.sink = resolveSink();

    this.restore();
    this.markerLocked("process-start");
    if (this.sink) this.tapPaywallFetchTelemetry();

    registerAPITrafficObserver((record) => this.record(record));

    // Phase boundaries come from the APP's own lifecycle observation, never from the SDK
    // under test — the suite asserts what the SDK did between two transitions, so the
    // boundary must not be produced by the machinery being measured. On web this is the
    // only option anyway: core has no lifecycle layer at all (NAM-4589).
    document.addEventListener("visibilitychange", () => {
      this.markerLocked(document.hidden ? "background" : "foreground");
    });

    // The tail of a cold start can still be in flight when the browser closes. `pagehide` is the
    // last point a beacon is still allowed to leave.
    window.addEventListener("pagehide", () => this.publishNow());
  }

  /**
   * Copies the SDK's `Paywall fetch telemetry:` line into the transcript as a marker.
   *
   * Core has no public log sink (Apple's test app registers a `NamiLoggerClient`; Android's hook
   * scrapes logcat), and its logger writes to the console, so the console is where the line is
   * caught — the web analogue of the logcat scrape. Installed only under the e2e harness
   * (`?transcriptSink=`); a marker is an annotation, never a phase boundary.
   */
  private tapPaywallFetchTelemetry(): void {
    const original = console.debug.bind(console);
    console.debug = (...args: unknown[]) => {
      const text = args.map(String).join(" ");
      const at = text.indexOf(PAYWALL_FETCH_TELEMETRY);
      if (at !== -1) this.markerLocked("paywall-fetch-telemetry", { message: text.slice(at).trim() });
      original(...args);
    };
  }

  /** Restores a previous page load's transcript so a reload does not lose the history. */
  private restore(): void {
    try {
      const raw = window.localStorage.getItem(TRANSCRIPT_KEY);
      if (!raw) return;
      const loaded = JSON.parse(raw) as TranscriptEntry[];
      if (!Array.isArray(loaded)) return;
      this.transcript = loaded;
      this.seq = loaded.reduce((max, e) => Math.max(max, (e.seq ?? -1) + 1), 0);
    } catch {
      // A corrupt or unavailable transcript is not worth failing the page over.
    }
  }

  private record(record: NamiAPITrafficRecord): void {
    const row: TrafficRow = { ...record, seq: this.seq };

    this.rows = [row, ...this.rows];
    if (this.rows.length > RECORD_LIMIT) {
      this.rows = this.rows.slice(0, RECORD_LIMIT);
      this.dropped = true;
    }

    this.append({
      seq: this.seq,
      timestampMs: record.timestamp.getTime(),
      kind: record.kind,
      method: record.method,
      url: record.url,
      path: record.path,
      statusCode: record.statusCode,
      durationMs: record.durationMs,
    });

    this.seq += 1;
    this.emit();
  }

  /**
   * Writes a marker into the transcript. Not shown in the tab.
   *
   * Two kinds, and the difference matters to the assertion engine: `process-start`,
   * `background`, `foreground` and `reconfigure` are BOUNDARY markers that cut the
   * transcript into phases, while `configure-result` is an annotation read by the
   * `configure_state` predicate within whichever phase it falls in. Deliberately NOT a
   * boundary — it would cut a cold start in half at the point `configure()` returned,
   * which is the middle of the startup sequence the phase exists to assert.
   */
  mark(name: string, payload: Partial<TranscriptEntry> = {}): void {
    this.markerLocked(name, payload);
  }

  /** Records that a configure call completed, with the state it returned. */
  markConfigureResult(state: string, reconfigure = false): void {
    if (reconfigure) this.markerLocked("reconfigure");
    this.markerLocked("configure-result", { state });
  }

  private markerLocked(name: string, payload: Partial<TranscriptEntry> = {}): void {
    this.append({
      ...payload,
      seq: this.seq,
      timestampMs: Date.now(),
      kind: "marker",
      marker: name,
    });
    this.seq += 1;
  }

  private append(entry: TranscriptEntry): void {
    this.transcript.push(entry);
    if (typeof window === "undefined") return;
    window.__namiApiTranscript = this.transcript;
    try {
      window.localStorage.setItem(TRANSCRIPT_KEY, JSON.stringify(this.transcript));
    } catch {
      // Quota or a private window. The in-memory handle above is still readable, which is
      // what the harness reads; persistence across reloads is the only thing lost.
    }
    this.schedulePublish();
  }

  /** Queues a publish, coalescing the burst a cold start produces into one POST per window. */
  private schedulePublish(): void {
    if (!this.sink || this.publishTimer) return;
    this.publishTimer = setTimeout(() => {
      this.publishTimer = null;
      this.publishNow();
    }, PUBLISH_DEBOUNCE_MS);
  }

  /**
   * Sends the whole transcript to the sink, replacing whatever it holds.
   *
   * `sendBeacon` with a `text/plain` body so there is no CORS preflight and the send survives the
   * page going away; `fetch` as a fallback where it is unavailable. Failures are swallowed — a
   * page must not break because no harness is listening.
   */
  private publishNow(): void {
    if (!this.sink) return;
    const body = JSON.stringify({ rev: this.publishRev++, transcript: this.transcript });
    try {
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        // sendBeacon returns false when the UA refuses to QUEUE the payload — Chrome enforces a
        // ~64 KiB quota on the SUM of all in-flight beacons, and this publishes the whole
        // transcript every PUBLISH_DEBOUNCE_MS during the cold-start burst, so in-flight bytes
        // grow quadratically across the burst. A `false` return means the body never left, so it
        // must fall through to `fetch` rather than return as if it had.
        if (navigator.sendBeacon(this.sink, new Blob([body], { type: "text/plain" }))) return;
      }
    } catch {
      // Fall through to fetch.
    }
    // `keepalive` shares the same ~64 KiB in-flight quota, and a body over it is REJECTED rather than
    // queued — which is every publish once a transcript spans a few loads (the paywall-cache suite's
    // tc03 reaches ~70 KB on its third). Past the quota, send an ordinary request: it cannot outlive
    // the page, but the debounced publishes during the load are what the harness reads (NAM-4357).
    const keepalive = body.length < KEEPALIVE_BODY_LIMIT;
    void fetch(this.sink, {
      method: "POST",
      body,
      headers: { "Content-Type": "text/plain" },
      keepalive,
    }).catch(() => {
      // See above.
    });
  }

  /** Clears the visible rows and the transcript, and reopens a new process segment. */
  clear(): void {
    this.rows = [];
    this.transcript = [];
    this.seq = 0;
    this.dropped = false;
    if (typeof window !== "undefined") {
      window.__namiApiTranscript = this.transcript;
      try {
        window.localStorage.removeItem(TRANSCRIPT_KEY);
      } catch {
        // Nothing to do — the in-memory transcript is already cleared.
      }
    }
    this.markerLocked("process-start");
    this.emit();
    this.publishNow();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.rows);
    return () => {
      this.listeners.delete(listener);
    };
  }

  hasDroppedRecords(): boolean {
    return this.dropped;
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.rows);
  }
}

/** Reads the sink URL out of the page URL. Anything that is not an http(s) URL is ignored. */
function resolveSink(): string | null {
  try {
    const raw = new URL(window.location.href).searchParams.get(SINK_PARAM);
    if (!raw) return null;
    const parsed = new URL(raw);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

export const apiTrafficStore = new ApiTrafficStore();
